// Oosler / Woosler runner.
//
// Per Par-3 rules (spec):
//   1. Selection is made live during the round — the player who hit the
//      green closest from the tee is the "Oosler" for that hole. If
//      no one's on the green, "None" is selected and the hole becomes
//      a carry.
//   2. With a selected Oosler: par-or-better wins points from each
//      opponent; worse-than-par pays points to each opponent.
//   3. Birdie override: ANY other player making birdie on that hole
//      steals the points instead. If multiple birdies, the LAST birdie
//      in player order steals.
//   4. Carry: a "None" selection (or a Par 3 with no selection yet)
//      adds its base points to the next Par 3's pot. The carry counter
//      keeps accumulating until a Par 3 settles cleanly.
//
// "Points" here are point-units, not currency — a 1 in the result
// equals one player-paying-one-player. The leaderboard can later map
// units to a money amount via a future bet option; for now this runner
// just returns the unit-net per player.
//
// Pure function, same shape as the other runners. No React, no state.

import { Course, Hole, Player, ScoreMap } from "../types";

export interface OoslerWooslerOptions {
  // Reserved per-hole base unit. Always 1 in practice — the catalog
  // doesn't expose this; left in the interface for future flexibility.
  basePoints?: number;
  // Currency rate per intrinsic point unit. Every per-player point
  // delta the runner produces is multiplied by this before it's
  // returned, so the leaderboard renders straight currency. Default
  // (when omitted) is 1, matching legacy unit-as-dollar behaviour.
  pointBet?: number;
}

export interface OoslerWooslerPayout {
  // Net point units for the round. Positive = up; negative = down.
  points: number;
  // Per-hole detail rows for the leaderboard's hole-expansion view.
  // `hole` is 1-based; `delta` is the net for THIS player on that hole.
  // Holes that carried forward (no settle) emit no row.
  details: Array<{ hole: number; delta: number; note: string }>;
}

export interface OoslerWooslerResult {
  payouts: Record<string, OoslerWooslerPayout>;
  // 1-based hole number → stake that rolled forward, populated only
  // for Par-3 holes that didn't settle this round (None selection,
  // selection's score not yet entered, or no selection at all).
  // Powers the scorecard's per-hole "Carry (N)" badge.
  carries: Record<number, number>;
  // Ordered list of Par-3 hole numbers in course order. The leaderboard
  // matrix uses this for column headers; the scorecard already iterates
  // course.holes directly so it doesn't need this.
  par3Holes: number[];
  // [holeNumber][playerId] → that player's net point delta on that
  // hole. Carry / unresolved holes have 0 for every player so the
  // matrix can render a full grid without missing cells.
  perHole: Record<number, Record<string, number>>;
}

export interface OoslerWooslerInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  selections: Record<number, string | null>;
  options: OoslerWooslerOptions;
}

export function runOoslerWoosler(
  input: OoslerWooslerInput,
): OoslerWooslerResult {
  const { course, players, scores, selections, options } = input;
  const basePoints = Number(options.basePoints) || 1;

  // Initialize per-player payout buckets.
  const payouts: Record<string, OoslerWooslerPayout> = {};
  for (const p of players) {
    payouts[p.id] = { points: 0, details: [] };
  }

  // Iterate Par 3 holes in course order. Carry counter accumulates the
  // pot from any None / unsettled hole into the next Par 3. perHole is
  // pre-seeded with 0 for every (player, par-3 hole) cell so carry /
  // unresolved holes render a full row of zeros in the leaderboard
  // matrix without any caller-side conditional logic.
  const par3Holes = course.holes.filter((h) => h.par === 3);
  const par3Numbers = par3Holes.map((h) => h.number);
  let carry = 0;
  const carries: Record<number, number> = {};
  const perHole: Record<number, Record<string, number>> = {};
  for (const h of par3Holes) {
    perHole[h.number] = {};
    for (const p of players) perHole[h.number][p.id] = 0;
  }

  const noteFor = (h: Hole, body: string) =>
    `Hole ${h.number} (Par 3): ${body}`;

  for (const hole of par3Holes) {
    const stake = basePoints + carry;
    const selection = selections[hole.number];

    // Helper — gross score for a player on this hole or undefined.
    const grossFor = (pid: string) => {
      const sc = scores[pid]?.[hole.number];
      return sc && typeof sc.gross === "number" ? sc.gross : undefined;
    };

    // Carry the pot when "None" was selected OR when the hole hasn't
    // been resolved yet (no selection recorded). No settlement, no
    // detail row. We DO record the hole in `carries` so the scorecard
    // can render the carry badge on the par-3's par cell.
    if (selection === null || selection === undefined) {
      carry = stake;
      carries[hole.number] = stake;
      continue;
    }

    const oosler = selection;
    const ooslerScore = grossFor(oosler);

    // Selected player hasn't entered their score yet — defer settling
    // (carry the stake to the next Par 3 same as None). When the score
    // later arrives, the next recompute will pick it up.
    if (ooslerScore === undefined) {
      carry = stake;
      carries[hole.number] = stake;
      continue;
    }

    // Birdie override — any OTHER scored player on this hole at par-1
    // (or better) steals the pot. Multiple birdies: the LAST in player
    // order wins, matching the spec.
    let birdieStealer: string | null = null;
    for (const p of players) {
      if (p.id === oosler) continue;
      const g = grossFor(p.id);
      if (g !== undefined && g <= hole.par - 1) {
        birdieStealer = p.id; // last-wins → keep overwriting
      }
    }

    if (birdieStealer) {
      // Stealer takes `stake` from each other player (incl. the Oosler).
      const opponents = players.filter((p) => p.id !== birdieStealer);
      const stealerDelta = stake * opponents.length;
      payouts[birdieStealer].points += stealerDelta;
      payouts[birdieStealer].details.push({
        hole: hole.number,
        delta: stealerDelta,
        note: noteFor(hole, `birdie steals ${stake} from each`),
      });
      perHole[hole.number][birdieStealer] = stealerDelta;
      for (const op of opponents) {
        payouts[op.id].points -= stake;
        payouts[op.id].details.push({
          hole: hole.number,
          delta: -stake,
          note: noteFor(hole, `birdie stolen by ${birdieStealer}`),
        });
        perHole[hole.number][op.id] = -stake;
      }
      carry = 0;
      continue;
    }

    // No birdie override — settle on the Oosler's score vs par.
    const opponents = players.filter((p) => p.id !== oosler);
    if (ooslerScore <= hole.par) {
      // Oosler par (or better) → wins `stake` from each opponent.
      const winDelta = stake * opponents.length;
      payouts[oosler].points += winDelta;
      payouts[oosler].details.push({
        hole: hole.number,
        delta: winDelta,
        note: noteFor(hole, `Oosler par — wins ${stake} from each`),
      });
      perHole[hole.number][oosler] = winDelta;
      for (const op of opponents) {
        payouts[op.id].points -= stake;
        payouts[op.id].details.push({
          hole: hole.number,
          delta: -stake,
          note: noteFor(hole, `paid ${stake} to Oosler`),
        });
        perHole[hole.number][op.id] = -stake;
      }
    } else {
      // Woosler — Oosler fails par → pays `stake` to each opponent.
      const loseDelta = -stake * opponents.length;
      payouts[oosler].points += loseDelta;
      payouts[oosler].details.push({
        hole: hole.number,
        delta: loseDelta,
        note: noteFor(hole, `Woosler — pays ${stake} to each opponent`),
      });
      perHole[hole.number][oosler] = loseDelta;
      for (const op of opponents) {
        payouts[op.id].points += stake;
        payouts[op.id].details.push({
          hole: hole.number,
          delta: stake,
          note: noteFor(hole, `received ${stake} from Woosler`),
        });
        perHole[hole.number][op.id] = stake;
      }
    }
    carry = 0;
  }

  // Scale unit-point deltas by the configured bet rate so everything
  // the leaderboard renders is already in currency. Default of 1 keeps
  // the legacy unit-as-dollar behaviour for rounds that don't set
  // pointBet. Multi-stage scaling (payouts.points, every detail.delta,
  // every perHole cell, and the carries) so a single rate flows
  // cleanly through every consumer downstream.
  const pointBet = Number(options.pointBet);
  const scale = Number.isFinite(pointBet) && pointBet > 0 ? pointBet : 1;
  if (scale !== 1) {
    for (const p of players) {
      payouts[p.id].points *= scale;
      payouts[p.id].details = payouts[p.id].details.map((d) => ({
        ...d,
        delta: d.delta * scale,
      }));
    }
    for (const h of par3Numbers) {
      for (const p of players) {
        perHole[h][p.id] = (perHole[h][p.id] || 0) * scale;
      }
    }
    for (const h of Object.keys(carries)) {
      const n = parseInt(h, 10);
      carries[n] = carries[n] * scale;
    }
  }

  return { payouts, carries, par3Holes: par3Numbers, perHole };
}
